/**
 * Client Detail View
 * Display complete client information with related cases and visits
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Edit, Trash2, ArrowLeft, User, MapPin, Phone, DollarSign, Briefcase, Calendar } from 'lucide-react'

import { useClient, useClientCases, useClientVisits, useDeleteClient } from '@/hooks'
import { toast } from '@/lib/toast'
import { PageLoading } from '@/components/loading'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Fetch client data
  const { data: client, isLoading, error } = useClient(id!)
  const { data: cases, isLoading: isLoadingCases } = useClientCases(id!)
  const { data: visits, isLoading: isLoadingVisits } = useClientVisits(id!)

  // Delete mutation
  const deleteClient = useDeleteClient()

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteClient.mutateAsync(id!)
      toast.success('Client deleted successfully')
      navigate('/clients')
    } catch (error: any) {
      toast.error('Failed to delete client', error.message)
    }
  }

  // Calculate age
  const calculateAge = (birthday: string | null | undefined): string => {
    if (!birthday) return 'N/A'
    const birthDate = new Date(birthday)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return `${age - 1} years old`
    }
    return `${age} years old`
  }

  // Format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Format full name
  const formatName = () => {
    const parts = [client?.firstname, client?.middlename, client?.lastname].filter(Boolean)
    return parts.join(' ') || 'N/A'
  }

  // Format address
  const formatAddress = () => {
    const parts = [
      client?.street_name,
      client?.barangay,
      client?.city,
      client?.province,
      client?.postal_code,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : 'N/A'
  }

  if (isLoading) {
    return <PageLoading message="Loading client details..." />
  }

  if (error || !client) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-red-600">Client not found</p>
          <Link to="/clients">
            <Button className="mt-4 bg-primary">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Clients
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/clients">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-primary">{formatName()}</h1>
            <p className="text-gray-600 mt-1">Client ID: {client.id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/clients/${id}/edit`}>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cases">
            Cases {cases && `(${cases.length})`}
          </TabsTrigger>
          <TabsTrigger value="visits">
            Visits {visits && `(${visits.length})`}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem label="Full Name" value={formatName()} />
              <InfoItem label="Gender" value={client.gender} />
              <InfoItem label="Birthday" value={formatDate(client.birthday)} />
              <InfoItem label="Age" value={calculateAge(client.birthday)} />
              <InfoItem label="Civil Status" value={client.civil_status} />
              <InfoItem label="Religion" value={client.religion} />
              <InfoItem label="Citizenship" value={client.citizenship} />
              <InfoItem label="Educational Attainment" value={client.educational_attainment} />
              <InfoItem label="Indigenous Group" value={client.indigenous_group} />
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <Phone className="h-5 w-5" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem label="Email" value={client.email} />
              <InfoItem label="Contact Number" value={client.contact_number} />
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <InfoItem label="Complete Address" value={formatAddress()} />
            </div>
          </div>

          {/* Economic Information */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5" />
              Economic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem
                label="Monthly Income"
                value={
                  client.monthly_income
                    ? `PHP ${client.monthly_income.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : 'N/A'
                }
              />
            </div>
          </div>
        </TabsContent>

        {/* Cases Tab */}
        <TabsContent value="cases">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <Briefcase className="h-5 w-5" />
              Client Cases
            </h2>
            {isLoadingCases ? (
              <p className="text-gray-500">Loading cases...</p>
            ) : cases && cases.length > 0 ? (
              <div className="space-y-4">
                {cases.map((caseItem: any) => (
                  <div key={caseItem.id} className="p-4 border rounded-lg">
                    <h3 className="font-semibold">{caseItem.case_title || 'Untitled Case'}</h3>
                    <p className="text-sm text-gray-600">Case Number: {caseItem.case_number}</p>
                    <p className="text-sm text-gray-600">Status: {caseItem.status}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No cases found for this client</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Visits Tab */}
        <TabsContent value="visits">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5" />
              Client Visits
            </h2>
            {isLoadingVisits ? (
              <p className="text-gray-500">Loading visits...</p>
            ) : visits && visits.length > 0 ? (
              <div className="space-y-4">
                {visits.map((visit: any) => (
                  <div key={visit.id} className="p-4 border rounded-lg">
                    <p className="font-semibold">Control Number: {visit.control_number || 'N/A'}</p>
                    <p className="text-sm text-gray-600">
                      Date: {formatDate(visit.visit_date || visit.created_at)}
                    </p>
                    <p className="text-sm text-gray-600">Status: {visit.status}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No visits found for this client</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {formatName()}? This action cannot be undone.
              All associated data (cases, visits) may also be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Helper component for displaying info items
function InfoItem({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-base text-gray-900">{value || 'N/A'}</p>
    </div>
  )
}

export default ClientDetail
